import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useStudentAuth } from '../hooks/useStudentAuth';
import { useStudentAccess } from '../hooks/useStudentAccess';
import { useBankTransferInfo } from '../hooks/useBankTransferInfo';
import { supabase } from '../lib/supabase';
import { BookOpen, CreditCard, MessageCircle, Loader2, AlertCircle, LogOut, Landmark } from 'lucide-react';

const PAYING_TIMEOUT_MS = 30_000;

// Diese Seiten bleiben auch bei gesperrtem Zugang erreichbar: Der Schüler
// muss zahlen können (inkl. Rechnungs-Download) und behält Zugriff auf
// seine eigenen Daten (DSGVO).
const ALLOWED_WHILE_BLOCKED = ['/dashboard/zahlungen', '/dashboard/profil'];

interface Props {
  children: React.ReactNode;
}

const ProtectedStudentRoute: React.FC<Props> = ({ children }) => {
  const { user, profile, loading: authLoading, signOut } = useStudentAuth();
  const { hasAccess, hasPendingPayment, planId, loading: accessLoading, error: accessError } = useStudentAccess({ user, profile, authLoading });
  const navigate = useNavigate();
  const location = useLocation();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const bankInfo = useBankTransferInfo(!accessLoading && !hasAccess && hasPendingPayment);

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // Safety timeout: if Stripe redirect silently fails, re-enable the button
  useEffect(() => {
    if (!paying) return;
    const t = setTimeout(() => {
      setPaying(false);
      setPayError('Weiterleitung zu Stripe hat zu lange gedauert. Bitte versuche es erneut.');
    }, PAYING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [paying]);

  if (authLoading || accessLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
        <img
          src="/LogoHeader4.png"
          alt="Arabicroots"
          className="h-10 w-auto object-contain opacity-60"
          onError={e => (e.currentTarget.style.display = 'none')}
        />
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#eadfd6] border-t-[#970000]" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!profile) return <Navigate to="/login" replace />;

  if (profile.role === 'admin') return <>{children}</>;

  if (!profile.is_active) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="mb-8">
          <img
            src="/LogoHeader4.png"
            alt="Arabicroots"
            className="h-12 w-auto object-contain mx-auto"
            onError={e => (e.currentTarget.style.display = 'none')}
          />
        </div>
        <div className="max-w-sm w-full bg-white rounded-[28px] border border-[#eadfd6] shadow-[0_16px_48px_rgba(0,0,0,0.08)] p-8 text-center">
          <div className="w-14 h-14 bg-[#faf7f5] border border-[#eadfd6] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <BookOpen className="h-7 w-7 text-[#8a8178]" />
          </div>
          <h2 className="text-xl font-bold text-[#111827] mb-2">Konto wird aktiviert</h2>
          <p className="text-sm text-[#6b6b6b] leading-relaxed mb-7">
            Dein Konto wurde noch nicht aktiviert. Bitte warte, bis wir deinen Zugang
            freischalten. Bei Fragen melde dich gerne bei uns.
          </p>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-[#9b9490] hover:text-[#5f5148] transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Mit anderem Konto anmelden
          </button>
        </div>
      </div>
    );
  }

  // Zugriffsprüfung fehlgeschlagen (z. B. Netzwerkfehler): weder Zugang gewähren
  // noch fälschlich "Zahlung erforderlich" anzeigen.
  if (accessError) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="mb-8">
          <img
            src="/LogoHeader4.png"
            alt="Arabicroots"
            className="h-12 w-auto object-contain mx-auto"
            onError={e => (e.currentTarget.style.display = 'none')}
          />
        </div>
        <div className="max-w-sm w-full bg-white rounded-[28px] border border-[#eadfd6] shadow-[0_16px_48px_rgba(0,0,0,0.08)] p-8 text-center">
          <div className="w-14 h-14 bg-[#faf7f5] border border-[#eadfd6] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-7 w-7 text-[#8a8178]" />
          </div>
          <h2 className="text-xl font-bold text-[#111827] mb-2">Etwas ist schiefgelaufen</h2>
          <p className="text-sm text-[#6b6b6b] leading-relaxed mb-7">
            Dein Zugang konnte nicht überprüft werden. Bitte lade die Seite neu
            oder versuche es später erneut.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-6 py-3.5 bg-[#970000] text-white rounded-2xl text-sm font-semibold hover:bg-[#7a0000] transition-colors mb-4"
          >
            Seite neu laden
          </button>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-[#9b9490] hover:text-[#5f5148] transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Mit anderem Konto anmelden
          </button>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    // Zahlungs- und Profilseite bleiben trotz Sperre zugänglich
    if (ALLOWED_WHILE_BLOCKED.some(p => location.pathname.startsWith(p))) {
      return <>{children}</>;
    }

    const handlePayWithStripe = async () => {
      if (!planId) return;
      setPaying(true);
      setPayError(null);
      try {
        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
          body: { plan_id: planId },
        });
        if (error) {
          let msg = 'Zahlung konnte nicht gestartet werden.';
          try {
            const raw = await (error as { context?: Response }).context?.text?.();
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed.error) msg = parsed.error;
            }
          } catch { /* ignore parse errors */ }
          setPayError(msg);
          setPaying(false);
          return;
        }
        if (data?.url) {
          // Only follow HTTPS Stripe-hosted URLs — protects against a compromised
          // edge function or corrupted response redirecting us to a phishing page.
          const isStripeUrl = /^https:\/\/(checkout|billing)\.stripe\.com\//.test(data.url);
          if (!isStripeUrl) {
            setPayError('Ungültige Zahlungs-URL erhalten. Bitte kontaktiere den Support.');
            setPaying(false);
            return;
          }
          window.location.href = data.url;
          // Don't reset paying here — redirect is in progress
        } else {
          setPayError(data?.error ?? 'Fehler beim Erstellen der Zahlungsseite.');
          setPaying(false);
        }
      } catch {
        setPayError('Netzwerkfehler. Bitte prüfe deine Internetverbindung und versuche es erneut.');
        setPaying(false);
      }
    };

    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        {/* Stripe redirect overlay */}
        {paying && (
          <div className="fixed inset-0 z-[9999] bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 text-[#970000] animate-spin" />
            <p className="text-sm font-semibold text-gray-700">Weiterleitung zu Stripe…</p>
          </div>
        )}

        {/* Logo */}
        <div className="mb-8">
          <img
            src="/LogoHeader4.png"
            alt="Arabicroots"
            className="h-12 w-auto object-contain mx-auto"
            onError={e => (e.currentTarget.style.display = 'none')}
          />
        </div>

        <div className="max-w-sm w-full space-y-3">
          {/* Main card */}
          <div className="bg-white rounded-[28px] border border-[#eadfd6] shadow-[0_16px_48px_rgba(0,0,0,0.08)] overflow-hidden">
            {/* Red accent bar */}
            <div className="h-1 bg-[#970000]" />

            <div className="p-8 text-center">
              <div className="w-14 h-14 bg-[#970000]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CreditCard className="h-7 w-7 text-[#970000]" />
              </div>

              <h2 className="text-2xl font-bold text-[#111827] mb-2">
                {hasPendingPayment ? 'Zahlung erforderlich' : 'Kein aktiver Zugang'}
              </h2>
              <p className="text-sm text-[#6b6b6b] leading-relaxed mb-7">
                {hasPendingPayment
                  ? 'Um Zugang zum Portal zu erhalten, ist eine Zahlung notwendig. Sobald deine Zahlung eingegangen ist, wird dein Zugang sofort freigeschaltet.'
                  : 'Für dein Konto ist derzeit kein aktiver Zugang hinterlegt. Bitte wende dich an deinen Lehrer, um deinen Zugang einzurichten.'}
              </p>

              {planId ? (
                <button
                  onClick={handlePayWithStripe}
                  disabled={paying}
                  className="w-full inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-[#970000] text-white rounded-2xl text-sm font-semibold hover:bg-[#7a0000] active:bg-[#600000] transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_4px_14px_rgba(151,0,0,0.25)]"
                >
                  {paying ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Weiterleitung…</>
                  ) : (
                    <><CreditCard className="h-4 w-4" /> Jetzt mit Stripe bezahlen</>
                  )}
                </button>
              ) : (
                <div className="w-full px-5 py-3.5 bg-[#faf7f5] border border-[#eadfd6] text-[#8a8178] rounded-2xl text-sm text-center">
                  Kein aktiver Zahlungsplan vorhanden. Bitte wende dich an deinen Lehrer.
                </div>
              )}

              {payError && (
                <div className="mt-4 flex items-start gap-2.5 text-sm text-[#970000] bg-[#970000]/5 border border-[#970000]/15 rounded-xl px-4 py-3 text-left">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>{payError}</p>
                </div>
              )}
            </div>
          </div>

          {/* Per Überweisung zahlen */}
          {bankInfo ? (
            <div className="bg-white rounded-[20px] border border-[#eadfd6] px-5 py-4">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="shrink-0 w-8 h-8 bg-[#faf7f5] rounded-xl flex items-center justify-center">
                  <Landmark className="h-3.5 w-3.5 text-[#8a8178]" />
                </div>
                <p className="text-sm font-semibold text-[#111827]">Oder per Überweisung zahlen</p>
              </div>
              <dl className="space-y-1.5 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="text-[#8a8178] shrink-0">Empfänger</dt>
                  <dd className="text-[#111827] font-semibold text-right">{bankInfo.account_holder}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-[#8a8178] shrink-0">IBAN</dt>
                  <dd className="text-[#111827] font-semibold text-right break-all">{bankInfo.iban}</dd>
                </div>
                {bankInfo.bic && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-[#8a8178] shrink-0">BIC</dt>
                    <dd className="text-[#111827] font-semibold text-right">{bankInfo.bic}</dd>
                  </div>
                )}
                {bankInfo.bank_name && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-[#8a8178] shrink-0">Bank</dt>
                    <dd className="text-[#111827] font-semibold text-right">{bankInfo.bank_name}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <dt className="text-[#8a8178] shrink-0">Verwendungszweck</dt>
                  <dd className="text-[#111827] font-semibold text-right">AR Kursgebühr</dd>
                </div>
              </dl>
              <p className="text-[11px] text-[#9b9490] leading-relaxed mt-3">
                Sobald deine Überweisung bei uns eingegangen ist, schalten wir deinen
                Zugang frei. Das kann 1–3 Werktage dauern.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-[20px] border border-[#eadfd6] px-5 py-4 flex items-start gap-3.5">
              <div className="shrink-0 w-8 h-8 bg-[#faf7f5] rounded-xl flex items-center justify-center mt-0.5">
                <MessageCircle className="h-3.5 w-3.5 text-[#8a8178]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#111827] mb-0.5">
                  Andere Zahlungswege?
                </p>
                <p className="text-xs text-[#6b6b6b] leading-relaxed">
                  Du möchtest bar, per Überweisung oder auf anderem Weg zahlen?
                  Kontaktiere deinen Lehrer – er kann deine Zahlung manuell erfassen
                  und deinen Zugang freischalten.
                </p>
              </div>
            </div>
          )}

          {/* Zahlungsübersicht bleibt trotz Sperre erreichbar */}
          <div className="text-center pt-1">
            <button
              onClick={() => navigate('/dashboard/zahlungen')}
              className="text-sm font-semibold text-[#970000] hover:underline"
            >
              Zahlungsübersicht & Rechnungen öffnen
            </button>
          </div>

          {/* Logout */}
          <div className="text-center">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-sm text-[#c8bfb6] hover:text-[#8a8178] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Mit anderem Konto anmelden
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedStudentRoute;
